/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/shell/ui/Avatar.tsx`.
// Inlined inside contacts/ so the shared package doesn't pull in
// app-side shell types. Same sizing + presence dot + auto-derived
// hue treatment as the web original.
//
// `lucide-react-native` icons take a `color` prop because RN-side
// icons don't inherit `currentColor`; the web `<img>` element is
// swapped for `<Image>` from react-native.

import { Image, Text, View } from "react-native";
import type { ContactPresence } from "./ContactsScreen.types";

export type ContactAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

interface ContactAvatarProps {
	readonly imageUri?: string | undefined;
	readonly name: string;
	readonly size?: ContactAvatarSize;
	readonly presence?: ContactPresence;
}

const SIZE_PX: Record<ContactAvatarSize, number> = {
	xs: 24,
	sm: 32,
	md: 40,
	lg: 48,
	xl: 64,
};

const DOT_PX: Record<ContactAvatarSize, number> = {
	xs: 6,
	sm: 8,
	md: 10,
	lg: 12,
	xl: 14,
};

const PRESENCE_COLOR: Record<NonNullable<ContactPresence>, string> = {
	online: "#34d399",
	away: "#fbbf24",
	busy: "#f87171",
	offline: "#71717a",
};

function initialsFor(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.map((w) => w[0] ?? "")
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

function hueFor(name: string): number {
	let h = 0;
	for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
	return h % 360;
}

export function ContactAvatar({
	imageUri,
	name,
	size = "md",
	presence = null,
}: ContactAvatarProps): JSX.Element {
	const px = SIZE_PX[size];
	const dotPx = DOT_PX[size];
	const hue = hueFor(name);
	const initials = initialsFor(name);

	return (
		<View
			accessibilityElementsHidden
			className="relative shrink-0"
			style={{ width: px, height: px }}
		>
			{imageUri !== undefined ? (
				<Image
					source={{ uri: imageUri }}
					accessibilityIgnoresInvertColors
					style={{ width: px, height: px, borderRadius: px / 2 }}
				/>
			) : (
				<View
					className="rounded-full flex items-center justify-center"
					style={{
						width: px,
						height: px,
						backgroundColor: `hsl(${hue}, 55%, 42%)`,
					}}
				>
					<Text
						className="font-sans font-semibold text-white"
						style={{ fontSize: Math.max(10, Math.round(px * 0.36)) }}
					>
						{initials}
					</Text>
				</View>
			)}
			{presence !== null && presence !== undefined && (
				<View
					className="absolute bottom-0 right-0 rounded-full"
					style={{
						width: dotPx,
						height: dotPx,
						borderWidth: 2,
						borderColor: "#FFFFFF",
						backgroundColor: PRESENCE_COLOR[presence],
					}}
				/>
			)}
		</View>
	);
}
